import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { Compass, Mic, PenLine, SmilePlus } from 'lucide-react';
import { useAppSettings } from './providers/AppSettingsProvider';
import { WhisperImmersiveProvider, useWhisperImmersive } from '../contexts/WhisperImmersiveContext';
import { NavItem } from '../types';
import {
  FAB_CENTER_BUTTON_BG,
  FAB_CENTER_BUTTON_BOTTOM_REM,
  FAB_CENTER_BUTTON_SIZE_REM,
  FAB_CENTER_BUTTON_TEXT,
  FAB_MENU_BOTTOM_ROW_BOTTOM_OFFSET_REM,
  FAB_MENU_PANEL_GAP_BELOW_REM,
  FAB_MENU_PANEL_HEIGHT_REM,
  FAB_MENU_PANEL_MAX_WIDTH_REM,
  FAB_MENU_TOP_BUTTON_TOP_OFFSET_REM,
  FAB_RADIAL_SIDE_OFFSET_REM,
  FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX,
  FAB_SIDE_BUTTONS_BG_DARK,
  FAB_SIDE_BUTTONS_BG_LIGHT,
  FAB_SIDE_BUTTONS_SHADOW,
  FAB_SIDE_BUTTONS_SIZE_REM,
  FAB_MAIN_CIRCLE_ICON_PX,
  SOLID_MIC_SVG_PATH_D,
  FAB_TAP_TO_TALK_BACKDROP_BLUR_PX,
  FAB_TAP_TO_TALK_BG_DARK,
  FAB_TAP_TO_TALK_BG_LIGHT,
  FAB_TAP_TO_TALK_BUTTON_SIZE_REM,
  FAB_TAP_TO_TALK_SHADOW,
  TAB_BAR_H_PX,
  TAB_BAR_TOP_EDGE_STROKE_DARK,
  TAB_BAR_TOP_EDGE_STROKE_LIGHT,
  TAB_BAR_TOP_EDGE_STROKE_WIDTH,
  tabBarClipPathD,
  tabBarTopEdgeD,
} from '../constants/tabBarFabWhisper';

interface LayoutProps {
  children: React.ReactNode;
  currentPageName?: string;
}

const AppContent: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, t } = useAppSettings();
  const { immersive: whisperImmersive, setImmersive: setWhisperImmersive } =
    useWhisperImmersive();
  const [fabExpanded, setFabExpanded] = useState(false);
  const tabBarClipId = useId().replace(/:/g, '');

  useEffect(() => {
    if (location.pathname !== '/whisper') {
      setWhisperImmersive(false);
    }
  }, [location.pathname, setWhisperImmersive]);

  useEffect(() => {
    setFabExpanded(false);
  }, [location.pathname]);

  // ==================== 导航栏配置 ====================
  const navItems: NavItem[] = [
    { icon: Mic, path: createPageUrl("WhisperTalk"), ariaKey: "home.nav.whisper" },
    { icon: '/images/svg/journal.svg', path: createPageUrl("Journals"), ariaKey: "home.nav.journals" },
    { icon: Compass, path: createPageUrl("ContentLibrary"), ariaKey: "home.nav.discover" },
    { icon: '/images/svg/profile.svg', path: createPageUrl("Profile"), ariaKey: "home.nav.profile" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const isStringIcon = typeof Icon === 'string';
    const isWhisperNav = item.path === createPageUrl('WhisperTalk');
    return (
      <Link
        key={item.path}
        to={item.path}
        aria-label={t(item.ariaKey)}
        className={`flex flex-col items-center justify-center w-16 h-16 rounded-2xl transition-all duration-300 ${active ? "text-[var(--text-primary)]" : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          }`}
      >
        <div className={`relative transition-transform ${active ? "scale-110" : "scale-100"}`}>
          {isStringIcon ? (
            <div
              className="w-7 h-7"
              style={{
                maskImage: `url(${Icon})`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: active ? '#FFFBAA' : 'white',
                WebkitMaskImage: `url(${Icon})`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
            />
          ) : isWhisperNav ? (
            <svg
              className="w-7 h-7"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ color: active ? '#FFFBAA' : 'white' }}
              aria-hidden
            >
              <path d={SOLID_MIC_SVG_PATH_D} />
            </svg>
          ) : (
            <Icon
              className="w-7 h-7"
              strokeWidth={active ? 2.5 : 2.5}
              style={{
                color: active ? '#FFFBAA' : 'white',
                stroke: active ? '#FFFBAA' : 'white'
              }}
            />
          )}
          {active && (
            <div className="absolute -inset-1 bg-[#FFFBAA]/45 blur-lg" />
          )}
        </div>
      </Link>
    );
  };

  // ==================== 处理子组件 ====================
  // 将翻译函数 t 传递给子组件（如果子组件需要）
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { t });
    }
    return child;
  });

  // ==================== 控制导航栏显示/隐藏 ====================
  // 某些页面（如引导页、问卷页、购买页）需要全屏显示，不需要底部导航栏
  const hideNav = location.pathname === '/splash'
    || location.pathname === '/onboarding'
    || location.pathname === '/purchase'
    || location.pathname === '/purchase-success'
    || location.pathname === '/onboarding-subscription'
    || location.pathname === '/journal-detail'
    || location.pathname.startsWith('/check-in')
    || (location.pathname === '/whisper' && whisperImmersive);

  return (
    <div className={`theme-provider ${theme}`}>
      <style>{`
        :root {
          --neon-blue: #00d4ff;
          --neon-purple: #b74dff;
          --neon-turquoise: #00fff5;
        }
        .theme-provider.dark {
          --bg: #0E1320;
          --card-bg: #131829;
          --text-primary: #E6EAF2;
          --text-secondary: #98A2B3;
          --text-tertiary: #6b7280;
          --border-color: #2a2f45;
          --accent-gradient: linear-gradient(to right, var(--neon-blue), var(--neon-purple));
        }
        .theme-provider.light {
          --bg: #F7F8FB;
          --card-bg: #FFFFFF;
          --text-primary: #222222;
          --text-secondary: #555555;
          --text-tertiary: #667085;
          --border-color: #e5e7eb;
          --accent-gradient: linear-gradient(to right, #22c55e, #14b8a6);
        }
        body {
          background-color: var(--bg);
          color: var(--text-primary);
        }
      `}</style>
      {/* ==================== 主容器 ==================== */}
      {/* 
        Layout 的工作方式：
        1. Layout 是所有页面的外层包装器（在 App.tsx 中包裹 Routes）
        2. children 就是当前路由匹配的页面组件（如 Onboarding、Profile 等）
        3. Layout 提供了统一的主题、样式和导航栏
        4. 导航栏使用 fixed 定位，会叠加在页面内容上方（z-50）
      */}
      <div className={`${hideNav ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-[var(--bg)]`}>
        {/* ==================== 页面内容区域 ==================== */}
        {/* 
          - 如果 hideNav=true：全屏显示，overflow-hidden 防止滚动
          - 如果 hideNav=false：添加 pb-32（底部padding），为导航栏与中间凸起按钮留出空间
        */}
        <main className={`${hideNav ? 'h-screen overflow-hidden' : 'pb-32 min-h-screen'}`}>
          {/* 这里渲染实际的页面组件（如 Onboarding、Profile 等） */}
          {childrenWithProps}
        </main>

        {/* ==================== 底部导航栏 ==================== */}
        {/* 
          导航栏逻辑：
          1. 使用 fixed 定位（fixed bottom-0），固定在屏幕底部
          2. z-50 确保导航栏在最上层，会叠加在页面内容上
          3. 只有当 !hideNav 时才显示（普通页面显示，引导页/问卷页不显示）
          4. 一体式顶边：clipPath 半圆由 BUMP_R_PX 等常量驱动；总高 TAB_BAR_H_PX；主内容 pb-32
        */}
        {!hideNav && (
          <nav className="pointer-events-none fixed bottom-0 left-0 right-0 z-50 overflow-visible">
            {fabExpanded && (
              <button
                type="button"
                aria-label={t('home.fab.close')}
                className="pointer-events-auto fixed inset-0 z-[15] bg-[#0a1020]/45 backdrop-blur-[2px] dark:bg-black/50"
                onClick={() => setFabExpanded(false)}
              />
            )}
            <svg width={0} height={0} className="absolute" aria-hidden>
              <defs>
                <clipPath id={tabBarClipId} clipPathUnits="objectBoundingBox">
                  <path d={tabBarClipPathD} />
                </clipPath>
              </defs>
            </svg>
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-0 backdrop-blur-xl"
              style={{
                height: TAB_BAR_H_PX,
                backgroundColor:
                  theme === 'dark'
                    ? 'rgba(111, 161, 255, 0.12)'
                    : 'rgba(236, 241, 252, 0.4)',
                clipPath: `url(#${tabBarClipId})`,
                WebkitClipPath: `url(#${tabBarClipId})`,
              }}
            />
            <svg
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]"
              width="100%"
              style={{ height: TAB_BAR_H_PX }}
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                d={tabBarTopEdgeD}
                fill="none"
                stroke={
                  theme === 'dark'
                    ? TAB_BAR_TOP_EDGE_STROKE_DARK
                    : TAB_BAR_TOP_EDGE_STROKE_LIGHT
                }
                strokeWidth={TAB_BAR_TOP_EDGE_STROKE_WIDTH}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div
              className="pointer-events-auto relative z-[20] mx-auto max-w-lg px-6"
              style={{ height: TAB_BAR_H_PX }}
            >
              {fabExpanded && (
                <div
                  className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2"
                  style={{
                    marginBottom: `${FAB_MENU_PANEL_GAP_BELOW_REM}rem`,
                    height: `${FAB_MENU_PANEL_HEIGHT_REM}rem`,
                    width: `min(${FAB_MENU_PANEL_MAX_WIDTH_REM}rem, calc(100vw - 2rem))`,
                  }}
                >
                  <div className="relative h-full w-full">
                    <div
                      className="pointer-events-auto absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-1.5"
                      style={{ top: `${FAB_MENU_TOP_BUTTON_TOP_OFFSET_REM}rem` }}
                    >
                      <span className="text-center text-[11px] font-medium leading-tight text-white/95 drop-shadow-sm">
                        {t('home.fab.tapToTalk')}
                      </span>
                      <button
                        type="button"
                        aria-label={t('home.fab.tapToTalk')}
                        className="flex items-center justify-center rounded-full text-white transition-transform active:scale-95"
                        style={{
                          width: `${FAB_TAP_TO_TALK_BUTTON_SIZE_REM}rem`,
                          height: `${FAB_TAP_TO_TALK_BUTTON_SIZE_REM}rem`,
                          background:
                            theme === 'dark'
                              ? FAB_TAP_TO_TALK_BG_DARK
                              : FAB_TAP_TO_TALK_BG_LIGHT,
                          boxShadow: FAB_TAP_TO_TALK_SHADOW,
                          backdropFilter: `blur(${FAB_TAP_TO_TALK_BACKDROP_BLUR_PX}px) saturate(160%)`,
                          WebkitBackdropFilter: `blur(${FAB_TAP_TO_TALK_BACKDROP_BLUR_PX}px) saturate(160%)`,
                        }}
                        onClick={() => {
                          setFabExpanded(false);
                          navigate(createPageUrl('WhisperTalk'), {
                            state: { autoStartVoice: true },
                          });
                        }}
                      >
                        <svg
                          className="shrink-0 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden
                          style={{
                            width: FAB_MAIN_CIRCLE_ICON_PX,
                            height: FAB_MAIN_CIRCLE_ICON_PX,
                          }}
                        >
                          <path d={SOLID_MIC_SVG_PATH_D} />
                        </svg>
                      </button>
                    </div>
                    <div
                      className="pointer-events-auto absolute flex -translate-x-1/2 flex-col items-center gap-1.5"
                      style={{
                        left: `calc(50% - ${FAB_RADIAL_SIDE_OFFSET_REM}rem)`,
                        bottom: `${FAB_MENU_BOTTOM_ROW_BOTTOM_OFFSET_REM}rem`,
                      }}
                    >
                      <button
                        type="button"
                        aria-label={t('home.fab.checkIn')}
                        className="flex items-center justify-center rounded-full text-white transition-transform active:scale-95"
                        style={{
                          width: `${FAB_SIDE_BUTTONS_SIZE_REM}rem`,
                          height: `${FAB_SIDE_BUTTONS_SIZE_REM}rem`,
                          background:
                            theme === 'dark'
                              ? FAB_SIDE_BUTTONS_BG_DARK
                              : FAB_SIDE_BUTTONS_BG_LIGHT,
                          boxShadow: FAB_SIDE_BUTTONS_SHADOW,
                          backdropFilter: `blur(${FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX}px) saturate(160%)`,
                          WebkitBackdropFilter: `blur(${FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX}px) saturate(160%)`,
                        }}
                        onClick={() => {
                          setFabExpanded(false);
                          navigate(createPageUrl('CheckIn'));
                        }}
                      >
                        <SmilePlus className="h-7 w-7" strokeWidth={2} aria-hidden />
                      </button>
                      <span className="text-center text-[11px] font-medium leading-tight text-white/95 drop-shadow-sm">
                        {t('home.fab.checkIn')}
                      </span>
                    </div>
                    <div
                      className="pointer-events-auto absolute flex -translate-x-1/2 flex-col items-center gap-1.5"
                      style={{
                        left: `calc(50% + ${FAB_RADIAL_SIDE_OFFSET_REM}rem)`,
                        bottom: `${FAB_MENU_BOTTOM_ROW_BOTTOM_OFFSET_REM}rem`,
                      }}
                    >
                      <button
                        type="button"
                        aria-label={t('home.fab.typeChat')}
                        className="flex items-center justify-center rounded-full text-white transition-transform active:scale-95"
                        style={{
                          width: `${FAB_SIDE_BUTTONS_SIZE_REM}rem`,
                          height: `${FAB_SIDE_BUTTONS_SIZE_REM}rem`,
                          background:
                            theme === 'dark'
                              ? FAB_SIDE_BUTTONS_BG_DARK
                              : FAB_SIDE_BUTTONS_BG_LIGHT,
                          boxShadow: FAB_SIDE_BUTTONS_SHADOW,
                          backdropFilter: `blur(${FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX}px) saturate(160%)`,
                          WebkitBackdropFilter: `blur(${FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX}px) saturate(160%)`,
                        }}
                        onClick={() => {
                          setFabExpanded(false);
                          navigate(createPageUrl('WhisperTalk'), {
                            state: { autoStartTextChat: true },
                          });
                        }}
                      >
                        <PenLine className="h-7 w-7" strokeWidth={2} aria-hidden />
                      </button>
                      <span className="text-center text-[11px] font-medium leading-tight text-white/95 drop-shadow-sm">
                        {t('home.fab.typeChat')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="absolute inset-x-6 bottom-0 flex h-20 items-center justify-between gap-1">
                <div className="flex min-w-0 flex-1 justify-around">
                  {renderNavItem(navItems[0])}
                  {renderNavItem(navItems[1])}
                </div>
                <div className="w-16 shrink-0" aria-hidden />
                <div className="flex min-w-0 flex-1 justify-around">
                  {renderNavItem(navItems[2])}
                  {renderNavItem(navItems[3])}
                </div>
              </div>
              <button
                type="button"
                aria-label={fabExpanded ? t('home.fab.close') : t('home.nav.add')}
                aria-expanded={fabExpanded}
                className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center rounded-full shadow-lg transition-transform active:scale-95"
                style={{
                  bottom: `${FAB_CENTER_BUTTON_BOTTOM_REM}rem`,
                  width: `${FAB_CENTER_BUTTON_SIZE_REM}rem`,
                  height: `${FAB_CENTER_BUTTON_SIZE_REM}rem`,
                  backgroundColor: FAB_CENTER_BUTTON_BG,
                  color: FAB_CENTER_BUTTON_TEXT,
                }}
                onClick={() => setFabExpanded((v) => !v)}
              >
                <span className="translate-y-[-1px] text-[1.75rem] font-light leading-none">
                  {fabExpanded ? '×' : '+'}
                </span>
              </button>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};

const Layout: React.FC<LayoutProps> = ({ children, currentPageName }) => {
  return (
    <WhisperImmersiveProvider>
      <AppContent currentPageName={currentPageName}>
        {children}
      </AppContent>
    </WhisperImmersiveProvider>
  );
};

export default Layout;
